/**
 * ComplianceNoticeModal
 *
 * Two-tab modal that helps users:
 *   1. Send a formal Notice of Non-Compliance email (via mailto:) to the
 *      hospital's compliance team + local officials + media
 *   2. Share a pre-written X/Twitter post calling out non-compliance
 *
 * The email is opened in the user's own client — this platform never sends
 * on their behalf, which keeps CAN-SPAM liability with the sender.
 */

import { useState } from "react";
import { useBrand } from "@/config/brands";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Mail,
  Copy,
  ExternalLink,
  CheckCircle2,
  Twitter,
  AlertTriangle,
  Info,
  Gavel,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ComplianceHospital {
  id: string;
  name: string;
  city: string;
  state: string;
  bed_count?: number | null;
  cpt_count?: number | null;
  drg_count?: number | null;
  revenue_count?: number | null;
  hcpcs_count?: number | null;
  ndc_count?: number | null;
  has_uploaded_file?: boolean;
  total_procedures?: number | null;
}

interface ComplianceNoticeModalProps {
  hospital: ComplianceHospital | null;
  isOpen: boolean;
  onClose: () => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const LAW_EFFECTIVE = "January 1, 2021";
const DAYS_LAW_ACTIVE = Math.floor(
  (Date.now() - new Date("2021-01-01").getTime()) / 86_400_000
);

/** Max daily fine under 45 CFR Part 180 */
function dailyFine(bedCount: number | null | undefined): string {
  if (!bedCount) return "$300–$5,500";
  if (bedCount < 30) return "$300";
  return `$${Math.min(bedCount * 10, 5500).toLocaleString()}`;
}

/** State AG health division contacts (major states + NAAG fallback) */
const STATE_AG: Record<string, { name: string; email: string; url: string }> = {
  AL: { name: "Alabama AG Consumer Protection", email: "consumer@ago.state.al.us", url: "https://www.alabamaag.gov" },
  AK: { name: "Alaska AG", email: "attorney.general@alaska.gov", url: "https://www.law.alaska.gov" },
  AZ: { name: "Arizona AG Health Care", email: "consumerinfo@azag.gov", url: "https://www.azag.gov" },
  CA: { name: "California AG Health Care Rights", email: "HealthCareRights@doj.ca.gov", url: "https://oag.ca.gov/health" },
  CO: { name: "Colorado AG Consumer Protection", email: "stop.fraud.co@coag.gov", url: "https://coag.gov" },
  CT: { name: "Connecticut AG Health", email: "attorney.general@ct.gov", url: "https://portal.ct.gov/AG" },
  FL: { name: "Florida AG Consumer Protection", email: "consumerservices@myfloridalegal.com", url: "https://www.myfloridalegal.com" },
  GA: { name: "Georgia AG Consumer Protection", email: "consumer@law.ga.gov", url: "https://law.georgia.gov" },
  IL: { name: "Illinois AG Health Care Bureau", email: "consumer@ilag.gov", url: "https://illinoisattorneygeneral.gov" },
  MA: { name: "Massachusetts AG Health Care Division", email: "ago@state.ma.us", url: "https://www.mass.gov/ago" },
  MI: { name: "Michigan AG Health Care Fraud", email: "miag@michigan.gov", url: "https://www.michigan.gov/ag" },
  MN: { name: "Minnesota AG Health Care", email: "consumer.protection@ag.state.mn.us", url: "https://www.ag.state.mn.us" },
  NC: { name: "North Carolina AG Consumer Protection", email: "ncdoj@ncdoj.gov", url: "https://www.ncdoj.gov" },
  NJ: { name: "New Jersey AG Division of Consumer Affairs", email: "askconsumeraffairs@lps.state.nj.us", url: "https://www.njconsumeraffairs.gov" },
  NY: { name: "New York AG Health Care Bureau", email: "health.care@ag.ny.gov", url: "https://ag.ny.gov/bureau/health-care-bureau" },
  OH: { name: "Ohio AG Health Care Unit", email: "consumer@ohioattorneygeneral.gov", url: "https://www.ohioattorneygeneral.gov" },
  PA: { name: "Pennsylvania AG Health Care", email: "consumer@attorneygeneral.gov", url: "https://www.attorneygeneral.gov" },
  TX: { name: "Texas AG Consumer Protection", email: "cpd@oag.texas.gov", url: "https://www.texasattorneygeneral.gov" },
  VA: { name: "Virginia AG Healthcare Fraud Unit", email: "consumer@oag.state.va.us", url: "https://www.ag.virginia.gov" },
  WA: { name: "Washington AG Consumer Protection", email: "consumer.protection@atg.wa.gov", url: "https://www.atg.wa.gov" },
};

// ── Email generators ──────────────────────────────────────────────────────────

function buildViolationList(hospital: ComplianceHospital): string[] {
  const violations: string[] = [];
  if (!hospital.has_uploaded_file || !hospital.total_procedures) {
    violations.push("No machine-readable chargemaster file found in our database");
  } else {
    if (!hospital.cpt_count) violations.push("Zero CPT procedure codes published (federal standard requires comprehensive coverage)");
    if (!hospital.drg_count) violations.push("Zero MS-DRG codes published (required for inpatient pricing disclosure)");
    if (!hospital.revenue_count) violations.push("Zero Revenue Codes published");
  }
  return violations;
}

function buildEmailSubject(hospital: ComplianceHospital): string {
  return `Notice of Non-Compliance — Hospital Price Transparency Rule (45 CFR Part 180) — ${hospital.name}`;
}

function buildEmailBody(
  hospital: ComplianceHospital,
  brandName: string,
  brandDomain: string
): string {
  const fine = dailyFine(hospital.bed_count);
  const violations = buildViolationList(hospital);
  const ag = STATE_AG[hospital.state];
  const violationLines = violations.map((v) => `  • ${v}`).join("\n");

  return `Dear ${hospital.name} Compliance / Legal Team,

This is a formal notice that ${hospital.name}, located in ${hospital.city}, ${hospital.state}, appears to be non-compliant with the Hospital Price Transparency Rule (45 CFR Part 180), which has been in federal effect since ${LAW_EFFECTIVE} — ${DAYS_LAW_ACTIVE.toLocaleString()} days ago as of today.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
OBSERVED COMPLIANCE ISSUES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Based on our data snapshot dated ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}:

${violationLines || "  • Chargemaster data appears incomplete or not publicly accessible"}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
REGULATORY REQUIREMENTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Under 45 CFR Part 180, ${hospital.name} is required to:
  1. Publicly post a machine-readable file containing standard charges for ALL items and services
  2. Display a consumer-friendly list of at least 300 shoppable services with pricing
  3. Ensure all information is accessible without requiring registration, login, or payment
  4. Update this information at least annually

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PENALTY EXPOSURE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
The Centers for Medicare & Medicaid Services (CMS) may impose civil monetary penalties of up to ${fine}/day for non-compliance (42 CFR § 180.70). This information is being forwarded to CMS and the ${ag ? ag.name : hospital.state + " Attorney General"} for review.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ACTION REQUESTED
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
We respectfully request that ${hospital.name} take immediate steps to come into full compliance with 45 CFR Part 180. Once compliance is verified, your hospital will be updated on our public tracking database at:

  ${brandDomain}/hospital/${hospital.id}

To report that you have come into compliance, or to provide documentation, please reply to this email.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
DISTRIBUTION
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
This notice is being copied to:
  • ${hospital.city} City Manager / Mayor's Office
  • ${hospital.state} Attorney General${ag ? " — " + ag.name : ""}
  • Local news organizations covering ${hospital.city}, ${hospital.state}
  • ${brandName} Public Compliance Record (${brandDomain}/data-sources)

This notice is sent pursuant to First Amendment rights to petition for government accountability and to promote healthcare price transparency for American patients and taxpayers.

Respectfully,
[Your Name]
[Your City, State]
[Your Email]

—
Sent via ${brandName} (${brandDomain})
Tracking price transparency compliance for 9,500+ US hospitals.
`;
}

function buildTweetText(hospital: ComplianceHospital, brandDomain: string): string {
  const fine = dailyFine(hospital.bed_count);
  const missing: string[] = [];
  if (hospital.has_uploaded_file && !hospital.drg_count) missing.push("DRG");
  if (hospital.has_uploaded_file && !hospital.cpt_count) missing.push("CPT");

  const missingStr = missing.length > 0 ? ` Missing: ${missing.join(", ")} codes.` : "";
  const noData = !hospital.has_uploaded_file || !hospital.total_procedures;

  const core = noData
    ? `${hospital.name} (${hospital.city}, ${hospital.state}) has not posted required hospital prices under federal law (45 CFR §180) — a law in effect since Jan 2021.`
    : `${hospital.name} (${hospital.city}, ${hospital.state}) has incomplete hospital price disclosure under federal law (45 CFR §180).${missingStr}`;

  const fine_line = `\n\nMax CMS fine: ${fine}/day. Collected from this hospital: $0.`;
  const link = `\n\n→ ${brandDomain}/hospital/${hospital.id}`;
  const tags = `\n\n#HealthcareTransparency #HospitalPrices #${hospital.state}`;

  const full = core + fine_line + link + tags;
  // Twitter limit is 280; truncate core if needed
  if (full.length <= 280) return full;
  const truncated = core.slice(0, core.length - (full.length - 277)) + "…";
  return truncated + fine_line + link + tags;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ComplianceNoticeModal({
  hospital,
  isOpen,
  onClose,
}: ComplianceNoticeModalProps) {
  const brand = useBrand();
  const { toast } = useToast();
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedTweet, setCopiedTweet] = useState(false);

  if (!hospital) return null;

  const ag = STATE_AG[hospital.state];
  const subject = buildEmailSubject(hospital);
  const body = buildEmailBody(hospital, brand.name, brand.primaryDomain);
  const tweetText = buildTweetText(hospital, brand.primaryDomain);
  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
  const fine = dailyFine(hospital.bed_count);

  // Build mailto: with all the CC addresses we can derive
  const ccAddresses = [
    `mayor@${hospital.city.toLowerCase().replace(/\s+/g, "")}.gov`,
    `citymanager@${hospital.city.toLowerCase().replace(/\s+/g, "")}.gov`,
    ag?.email,
  ].filter(Boolean).join(",");

  const mailtoHref =
    `mailto:?cc=${encodeURIComponent(ccAddresses)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const copyEmail = async () => {
    await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
    setCopiedEmail(true);
    toast({ title: "Notice copied to clipboard" });
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const copyTweet = async () => {
    await navigator.clipboard.writeText(tweetText);
    setCopiedTweet(true);
    toast({ title: "Tweet copied to clipboard" });
    setTimeout(() => setCopiedTweet(false), 2000);
  };

  const isAhp = brand.design === 'civic';
  const navy  = "var(--civic-navy)";
  const red   = "var(--civic-red)";
  const green = "var(--civic-money)";
  const gold  = "var(--civic-gold)";
  const border = "#e8d4b0";
  const serif = "'Instrument Serif', Georgia, serif";
  const sans  = "'Instrument Sans', system-ui, sans-serif";

  const violations = buildViolationList(hospital);
  const tweetCharCount = tweetText.length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        style={isAhp ? { backgroundColor: "#fff", fontFamily: sans } : undefined}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={isAhp ? { backgroundColor: `${red}15` } : { backgroundColor: "rgba(255,0,0,0.15)" }}
            >
              <Gavel className="w-4 h-4" style={isAhp ? { color: red } : { color: "#ff4444" }} />
            </div>
            <DialogTitle
              className={isAhp ? "text-xl" : "font-mono text-lg"}
              style={isAhp ? { color: "var(--civic-ink)", fontFamily: serif } : undefined}
            >
              Send Notice of Non-Compliance
            </DialogTitle>
          </div>

          {/* Hospital summary */}
          <div
            className="flex items-start gap-3 p-3 rounded-lg mt-2"
            style={isAhp ? { backgroundColor: `${red}08`, border: `1px solid ${red}20` } : { backgroundColor: "rgba(255,0,0,0.05)", border: "1px solid rgba(255,0,0,0.2)" }}
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={isAhp ? { color: red } : { color: "#ff4444" }} />
            <div className="min-w-0">
              <div className="font-semibold text-sm" style={isAhp ? { color: "var(--civic-ink)" } : undefined}>
                {hospital.name}
              </div>
              <div className="text-xs text-muted-foreground">{hospital.city}, {hospital.state}</div>
              <div className="flex gap-2 mt-1.5 flex-wrap">
                {violations.map((v, i) => (
                  <Badge key={i} className="text-xs" style={isAhp ? { backgroundColor: `${red}15`, color: red, border: `1px solid ${red}30` } : undefined}>
                    {v.split("(")[0].trim()}
                  </Badge>
                ))}
                <Badge className="text-xs" style={isAhp ? { backgroundColor: `${gold}20`, color: "#92710f", border: `1px solid ${gold}50` } : undefined}>
                  Est. fine: {fine}/day
                </Badge>
              </div>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="email" className="mt-2">
          <TabsList className="w-full">
            <TabsTrigger value="email" className="flex-1 gap-2">
              <Mail className="w-3.5 h-3.5" /> Email Notice
            </TabsTrigger>
            <TabsTrigger value="recipients" className="flex-1 gap-2">
              <Info className="w-3.5 h-3.5" /> Who to Contact
            </TabsTrigger>
            <TabsTrigger value="social" className="flex-1 gap-2">
              <Twitter className="w-3.5 h-3.5" /> Post on X
            </TabsTrigger>
          </TabsList>

          {/* ── Email Notice Tab ─────────────────────────────────────── */}
          <TabsContent value="email" className="space-y-4 mt-4">
            {/* Subject line */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                Subject Line
              </label>
              <div
                className="flex items-center gap-2 p-2.5 rounded text-xs font-mono break-all"
                style={isAhp ? { backgroundColor: "var(--civic-cream)", border: `1px solid ${border}` } : { backgroundColor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
              >
                {subject}
              </div>
            </div>

            {/* Body */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                Notice Template — Edit Before Sending
              </label>
              <Textarea
                value={body}
                readOnly
                rows={14}
                className="text-xs font-mono resize-y"
                style={isAhp ? { backgroundColor: "var(--civic-cream)", borderColor: border } : undefined}
              />
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={copyEmail}
                variant="outline"
                size="sm"
                className="gap-2"
                style={isAhp ? { borderColor: border } : undefined}
              >
                {copiedEmail ? <CheckCircle2 className="w-4 h-4" style={{ color: green }} /> : <Copy className="w-4 h-4" />}
                {copiedEmail ? "Copied!" : "Copy Notice"}
              </Button>
              <Button
                asChild
                size="sm"
                className="gap-2"
                style={isAhp ? { backgroundColor: navy, color: "#fff" } : undefined}
              >
                <a href={mailtoHref}>
                  <Mail className="w-4 h-4" />
                  Open in Email Client
                </a>
              </Button>
            </div>

            <p className="text-xs text-muted-foreground flex items-start gap-1.5">
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
              "Open in Email Client" uses your personal email — this platform never sends on your behalf.
              You must add the hospital's compliance officer email in the TO field.
              CC fields are pre-populated with city and state contacts.
            </p>
          </TabsContent>

          {/* ── Who to Contact Tab ───────────────────────────────────── */}
          <TabsContent value="recipients" className="space-y-4 mt-4">

            {/* Hospital compliance officer */}
            <div
              className="p-4 rounded-lg"
              style={isAhp ? { backgroundColor: "var(--civic-cream)", border: `1px solid ${border}` } : { border: "1px solid rgba(255,255,255,0.1)" }}
            >
              <div className="font-semibold text-sm mb-1" style={isAhp ? { color: navy } : undefined}>
                1. Hospital Compliance / Privacy Officer (TO:)
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                HIPAA requires every hospital to designate a Privacy Officer with a public contact. Find it via:
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button asChild size="sm" variant="outline" className="gap-1.5 text-xs">
                  <a href={`https://npiregistry.cms.hhs.gov/search?organization_name=${encodeURIComponent(hospital.name)}&city=${encodeURIComponent(hospital.city)}&state=${hospital.state}`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3 h-3" /> NPI Registry
                  </a>
                </Button>
                <Button asChild size="sm" variant="outline" className="gap-1.5 text-xs">
                  <a href={`https://www.google.com/search?q="${encodeURIComponent(hospital.name)}" compliance officer email`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3 h-3" /> Google Search
                  </a>
                </Button>
              </div>
            </div>

            {/* City contacts */}
            <div
              className="p-4 rounded-lg"
              style={isAhp ? { backgroundColor: "var(--civic-cream)", border: `1px solid ${border}` } : { border: "1px solid rgba(255,255,255,0.1)" }}
            >
              <div className="font-semibold text-sm mb-1" style={isAhp ? { color: navy } : undefined}>
                2. City Manager / Mayor's Office (CC:)
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Most city managers have a public email. Common patterns:
              </p>
              <div className="flex gap-1.5 flex-wrap">
                {[
                  `mayor@${hospital.city.toLowerCase().replace(/\s+/g, "")}.gov`,
                  `citymanager@${hospital.city.toLowerCase().replace(/\s+/g, "")}.gov`,
                  `city.manager@${hospital.city.toLowerCase().replace(/\s+/g, "")}.gov`,
                ].map((e) => (
                  <code key={e} className="text-xs px-2 py-0.5 rounded bg-muted">{e}</code>
                ))}
              </div>
            </div>

            {/* State AG */}
            <div
              className="p-4 rounded-lg"
              style={isAhp ? { backgroundColor: "var(--civic-cream)", border: `1px solid ${border}` } : { border: "1px solid rgba(255,255,255,0.1)" }}
            >
              <div className="font-semibold text-sm mb-1" style={isAhp ? { color: navy } : undefined}>
                3. State Attorney General Health Division (CC:)
              </div>
              {ag ? (
                <div className="space-y-1">
                  <p className="text-xs font-medium">{ag.name}</p>
                  <code className="text-xs px-2 py-0.5 rounded bg-muted block w-fit">{ag.email}</code>
                  <Button asChild size="sm" variant="outline" className="gap-1.5 text-xs mt-1">
                    <a href={ag.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-3 h-3" /> Visit {hospital.state} AG Website
                    </a>
                  </Button>
                </div>
              ) : (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Find your state AG's consumer/health division:
                  </p>
                  <Button asChild size="sm" variant="outline" className="gap-1.5 text-xs">
                    <a href="https://www.naag.org/find-my-ag/" target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-3 h-3" /> Find {hospital.state} AG — NAAG Directory
                    </a>
                  </Button>
                </div>
              )}
            </div>

            {/* Local media */}
            <div
              className="p-4 rounded-lg"
              style={isAhp ? { backgroundColor: "var(--civic-cream)", border: `1px solid ${border}` } : { border: "1px solid rgba(255,255,255,0.1)" }}
            >
              <div className="font-semibold text-sm mb-1" style={isAhp ? { color: navy } : undefined}>
                4. Local News Organizations (CC:)
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Find local investigative reporters and tip lines:
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button asChild size="sm" variant="outline" className="gap-1.5 text-xs">
                  <a href={`https://www.google.com/search?q=news+tip+line+${encodeURIComponent(hospital.city)}+${hospital.state}+newspaper`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3 h-3" /> Find {hospital.city} News Tip Lines
                  </a>
                </Button>
                <Button asChild size="sm" variant="outline" className="gap-1.5 text-xs">
                  <a href="https://www.ire.org/resources/tipsheets/" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3 h-3" /> IRE Investigative Reporters
                  </a>
                </Button>
              </div>
            </div>

            {/* CMS */}
            <div
              className="p-4 rounded-lg"
              style={isAhp ? { backgroundColor: `${green}08`, border: `1px solid ${green}30` } : { border: "1px solid rgba(0,255,0,0.15)" }}
            >
              <div className="font-semibold text-sm mb-1" style={isAhp ? { color: green } : { color: "#00cc00" }}>
                5. File a Formal CMS Complaint (Direct Enforcement)
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                CMS is the federal agency responsible for enforcement under 45 CFR Part 180.
              </p>
              <Button asChild size="sm" className="gap-1.5 text-xs" style={isAhp ? { backgroundColor: green, color: "#fff" } : undefined}>
                <a href="https://www.cms.gov/priorities/key-initiatives/hospital-price-transparency/submit-a-complaint" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-3 h-3" /> Submit CMS Complaint (Free)
                </a>
              </Button>
            </div>
          </TabsContent>

          {/* ── Social Media Tab ─────────────────────────────────────── */}
          <TabsContent value="social" className="space-y-4 mt-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  X / Twitter Post
                </label>
                <span
                  className={`text-xs font-mono ${tweetCharCount > 280 ? "text-red-500" : tweetCharCount > 250 ? "text-yellow-500" : "text-muted-foreground"}`}
                >
                  {tweetCharCount}/280
                </span>
              </div>

              {/* Tweet preview */}
              <div
                className="p-4 rounded-xl text-sm leading-relaxed whitespace-pre-wrap font-sans"
                style={isAhp
                  ? { backgroundColor: "var(--civic-cream)", border: `1px solid ${border}`, color: "var(--civic-ink)" }
                  : { backgroundColor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }
                }
              >
                {tweetText}
              </div>

              <div className="flex gap-2 mt-3 flex-wrap">
                <Button
                  onClick={copyTweet}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  style={isAhp ? { borderColor: border } : undefined}
                >
                  {copiedTweet ? <CheckCircle2 className="w-4 h-4" style={{ color: green }} /> : <Copy className="w-4 h-4" />}
                  {copiedTweet ? "Copied!" : "Copy Post"}
                </Button>
                <Button
                  asChild
                  size="sm"
                  className="gap-2"
                  style={{ backgroundColor: "#000", color: "#fff" }}
                >
                  <a href={tweetUrl} target="_blank" rel="noopener noreferrer">
                    <Twitter className="w-4 h-4" />
                    Post on X
                  </a>
                </Button>
              </div>

              <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1.5">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                Tagging local reporters and city officials increases impact. Add their handles after posting.
              </p>
            </div>

            {/* Suggested handles */}
            <div
              className="p-4 rounded-lg"
              style={isAhp ? { backgroundColor: "var(--civic-cream)", border: `1px solid ${border}` } : { border: "1px solid rgba(255,255,255,0.1)" }}
            >
              <div className="font-semibold text-sm mb-2" style={isAhp ? { color: navy } : undefined}>
                Suggested Accounts to Tag
              </div>
              <div className="grid grid-cols-2 gap-1.5 text-xs text-muted-foreground">
                <div>@CMSGov — CMS enforcement</div>
                <div>@KaiserHealthNews — Investigative</div>
                <div>@ProPublica — Investigative</div>
                <div>@ModernHealthcare — Trade press</div>
                <div>@POTUS — White House</div>
                <div>@SenFinance — Senate Finance Cmte</div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Also tag your state senator and local news station handles.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
